import { Immutable } from "immer";
import React from "react";
import { PDPChangeRootProductBlock } from "tsi-common-react/src/apps/configurator/PDPChangeRootProductBlock/index";
import { PDPChangeVariantOptionBlock } from "tsi-common-react/src/apps/configurator/PDPChangeVariantOptionBlock/index";
import { PDPSelectedRootProductConditionalBlock } from "tsi-common-react/src/apps/configurator/PDPSelectedRootProductConditionalBlock/index";
import { PDPSelectedVariantConditionalBlock } from "tsi-common-react/src/apps/configurator/PDPSelectedVariantConditionalBlock/index";
import { CompatibilityAccordion } from "tsi-common-react/src/components/CompatibilityAccordion/index";
import { RawHTMLBlock } from "tsi-common-react/src/components/RawHTMLBlock/index";
import { TabCardsBlock } from "tsi-common-react/src/components/TabCardsBlock/index";
import { ValuePropsBarBlock } from "tsi-common-react/src/components/ValuePropsBarBlock/index";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import { IOptionCode } from "tsi-common-react/src/models/catalogue";
import { isoProductID } from "tsi-common-react/src/models/nominals";
import * as streamfieldModels from "tsi-common-react/src/models/streamfield-blocks";
import { check } from "tsi-common-react/src/models/utils";

import { templates } from "@reactivated";
import type {
    CarouselModule as CarouselModuleValue,
    ChangeVariantOptionBlock as ChangeVariantOptionBlockValue,
    CompatibilityAccordionModule as CompatibilityAccordionValue,
    FullReviewsModule as FullReviewsModuleValue,
    RawHTMLBlock as RawHTMLBlockValue,
    SelectedRootProductConditionalBlock as RootConditionalBlockValue,
    TabCardsBlock as TabCardsBlockValue,
    ValuePropBarBlock as ValuePropBarBlockValue,
    SelectedVariantConditionalBlock as VariantConditionalBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { CarouselModule } from "../blocks/CarouselModule";
import { ToolboxPageSSR, buildToolboxPageMap } from "../blocks/toolboxPageMap";
import { Template as FeaturesBlock } from "../templates/FeaturesBlockTemplate";
import { Template as BundlesBlock } from "../templates/PDPBundlesTemplate";
import { DetailsModule } from "./DetailsModule";
import { FullReviewsModule } from "./FullReviewsModule";
import { SpotlightModule } from "./SpotlightModule";

/**
 * Shared assembly for the consolidated tempurpedic ProductDisplayPage /
 * ProductLinePage ``modules`` bodies (``pages.pdp.PDPModuleAssembly``).
 * This dispatches the page ``modules`` StreamField -- the hero chrome above the
 * body (the ``PDPHeroTemplate`` + ``OverlayBannerAssemblyTemplate`` +
 * ``{% modular_configurator %}``, and, in the alternative design, the hero's
 * own ``gallery_block`` / ``value_prop_bar_block`` extraction loops) is rendered
 * separately and is NOT part of this assembly.
 * ``ProductLinePage.configurator_based_modules`` is rendered through THIS same
 * assembly by ``ProductLinePageTemplate``'s alternative-design hero (via
 * ``{% include_block self.configurator_based_modules %}``), fed its own
 * ``cbm_*`` side-channels; it is out of scope of the ``modules`` body wrapper
 * (``ProductLinePageBody``) only.
 *
 * The base block set comes from ``buildToolboxPageMap`` -- the PAGE-AWARE toolbox
 * map (every TP Toolbox key, including the recursive ``promo_conditional_block``
 * / ``tabs_block`` and the SSR-envelope grids), built from the
 * ``get_toolbox_block_ssr`` side-channels so the six page/request/settings-
 * dependent toolbox keys (``hardcoded_block`` / ``hotspot_block`` /
 * ``breadcrumbs_block`` / ``customer_service_info_block`` / ``shop_compare_grid``
 * / ``tab_feature_attribute_block``) render with real props -- ``PDPModuleAssembly``
 * inherits all of ``Toolbox``, so any of them can appear in a PDP/PLP
 * ``modules`` (the base ``toolboxComponentsMap`` would render them empty/null).
 * Layered on top are the PDP module keys
 * (``bundles`` / ``carousel`` / ``compatibility`` / ``details`` / ``features`` /
 * ``HTML`` / ``reviews_full`` / ``spotlight`` / ``value_prop_bar``) and the
 * three configurator-driven keys (``change_variant_option_block`` /
 * ``root_product_conditional_block`` / ``variant_conditional_block``). The PDP
 * module keys dispatch to the existing TP Phase-1 components, which already
 * bake in the TP-specific rendering (Spotlight's empty
 * ``--`` brand-color suffix, Details' ``.rich-text`` rewrap + accordion classes,
 * FullReviews' ``starHasStroke``).
 *
 * Five blocks are page-dependent: the reactivated body-serialization path
 * threads ``request`` but not ``page``, so their server-computed SSR props are
 * threaded via id-keyed side-channels (``reviews`` / ``changeVariantSSR`` /
 * ``changeRootSSR`` / ``rootConditionalSSR`` / ``variantConditionalSSR``, built
 * by the ``get_pdp_reviews_props`` / ``get_pdp_block_ssr`` templatetags) and
 * looked up here by ``block.id``, plus two page-level scalars
 * (``pageNumReviews`` for the reviews CTA gate and
 * ``tabCardsInitialRootProductID`` for the tab-cards override). The
 * shared ``pdp-change-variant-option-block.html`` renders BOTH the change-ROOT
 * selector AND the change-VARIANT selector inside its ``#pdp-change-variant-
 * block`` wrapper, so a single ``change_variant_option_block`` carries TWO
 * id-keyed side-channel entries (``changeRootSSR`` + ``changeVariantSSR``) keyed
 * by the same ``block.id``. TP has NO standalone ``change_root_option_block``
 * (unlike sealy / stearns).
 *
 * The two conditional blocks dispatch their nested ``content`` recursively
 * through this SAME ``components`` map -- replacing the JSON
 * ``content_segments`` / ``dangerouslySetInnerHTML`` path so every nested block
 * hydrates. ``components`` is captured by reference (define-then-close-over),
 * exactly like the recursive ``promo_conditional_block``.
 */
/**
 * The page-derived SSR side-channels + scalars the PDP `modules` component map
 * closes over. Split out from `IProps` so the styleguide block-render registry
 * can build the same map with minimal (zeroed) props (the fixture path threads
 * no page).
 */
type IMapProps = Immutable<
    ToolboxPageSSR & {
        reviews: {
            [k: string]: templates.ProductReviewsTemplate;
        };
        pageNumReviews: number;
        changeVariantSSR: {
            [k: string]: templates.ChangeVariantOptionBlockTemplate;
        };
        changeRootSSR: {
            [k: string]: templates.ChangeRootProductBlockTemplate;
        };
        rootConditionalSSR: {
            [k: string]: templates.SelectedRootProductConditionalBlockTemplate;
        };
        variantConditionalSSR: {
            [k: string]: templates.SelectedVariantConditionalBlockTemplate;
        };
        tabCardsInitialRootProductID: number;
    }
>;

type IProps = IMapProps &
    Immutable<{
        value: NonNullable<
            templates.ProductLinePageTemplate["page"]["modules"]
        >["value"];
        /**
         * ProductLinePage mode: the legacy ``product_line_page.html`` rendered the
         * body via ``{% include_block self.modules %}`` (the whole-field Wagtail
         * ``render_basic`` path), which wraps every top-level block in
         * ``<div class="w-block-{type} block-{type}">`` -- the validated Phase-2
         * whole-field convention (BasicPage / HomePage). When set, this assembly
         * reproduces that per-top-level-block wrapper. The PDP (ProductDisplayPage)
         * legacy loop was a bare ``{% for ... include_block %}`` inside the
         * ``.pdp-blocks`` div, so it leaves this unset (``React.Fragment``). Nested
         * conditional ``content`` always renders unwrapped (both legacy paths
         * rendered it without per-block wrappers).
         */
        wrapBlocks?: boolean;
        /**
         * ProductDisplayPage design flag. The legacy ``product_display_page.html``
         * ``.pdp-blocks`` loop rendered EVERY module when ``use_default_design``,
         * but on the ALTERNATIVE design it SKIPPED top-level ``gallery_block`` and
         * ``value_prop_bar_block`` (those are extracted into the alt-design hero by
         * the template's own ``{% for ... include_block %}`` loops, which stay in
         * Django). When ``false`` this assembly filters those two top-level block
         * types out so they are not double-rendered. The PLP body does not have this
         * filter (its alt-design content lives in ``configurator_based_modules``,
         * out of scope), so it leaves this unset (defaults to default-design: render
         * everything).
         */
        useDefaultDesign?: boolean;
    }>;

/** Top-level block types the alt-design PDP hero extracts (see useDefaultDesign). */
const ALT_DESIGN_HERO_BLOCK_TYPES = new Set([
    "gallery_block",
    "value_prop_bar_block",
]);

/**
 * Build the PDP `modules` component map, closing over the page-derived SSR
 * side-channels in `props`. The recursive conditional entries capture the
 * `components` binding by reference (assigned before any entry is invoked), so
 * nested `content` dispatches through this same map -- exactly as when the map
 * was a function-local literal inside `ProductPageBody`.
 */
const buildProductPageBodyComponentsMap = (props: IMapProps) => {
    const components = {
        // Base = the PAGE-AWARE toolbox map (not the raw `toolboxComponentsMap`):
        // `PDPModuleAssembly` inherits all of `Toolbox`, so the six page/request/
        // settings-dependent toolbox keys (`hardcoded_block` / `hotspot_block` /
        // `breadcrumbs_block` / `customer_service_info_block` / `shop_compare_grid`
        // / `tab_feature_attribute_block`) can appear in a PDP/PLP `modules` (incl.
        // nested inside the PDP conditionals' `content`, which dispatch through
        // this same `components` map). `buildToolboxPageMap` threads the
        // `get_toolbox_block_ssr` side-channels so those blocks render with their
        // real page-derived props instead of the base map's empty/null defaults
        // (a null-render regression, here on a product page). Its own
        // recursive container entries (`tabs_block` / `promo_conditional_block`)
        // self-reference the page-aware toolbox map; the PDP-specific keys below
        // self-reference the outer `components`, so overrides apply at every depth.
        ...buildToolboxPageMap(props),

        // `bundles` -> TP PDPBundlesTemplate (the Out-of-context bundle
        // collection island). The block is a StaticBlock (empty value); only
        // `placeholder=""` is passed, which the component requires.
        bundles: () => <BundlesBlock placeholder="" />,

        // `carousel` -> TP CarouselModule. Builds the shared `Gallery`
        // directly from the serialized image renditions; same dispatch as the
        // legacy `pdp-carousel.html` -> PdpGalleryTemplate path (now reading
        // `block.value` instead of the JSON side-channel).
        carousel: (block: { value: Immutable<CarouselModuleValue> }) => (
            <CarouselModule value={block.value} />
        ),

        // `compatibility` -> CompatibilityAccordion wrapped in the themed
        // `<section>` that the standalone `compatibility-accordion-module.html`
        // emits (`<section class="{{ value.background_style.style }}">`), with
        // the `{% if value.compatibility_panels %}` gate reproduced (the
        // accordion only renders when there ARE panels; otherwise the section is
        // empty). The wrapper + gate MUST live here, NOT in the component, because
        // the consolidated dispatcher reaches the component raw (bypassing the
        // `.html`).
        compatibility: (block: {
            value: Immutable<CompatibilityAccordionValue>;
        }) => (
            <section className={block.value.background_style.style}>
                {(block.value.compatibility_panels ?? []).length > 0 && (
                    <CompatibilityAccordion value={block.value} />
                )}
            </section>
        ),

        // `details` -> TP DetailsModule (the 3-column "More Details"
        // accordion with `show_columns` initial state + `.rich-text` rewrap).
        details: DetailsModule,

        // `features` -> shared FeaturesBlock (the TP re-export; self-contained
        // `value` dispatch). In `PDPModuleAssembly` the `features` value is a
        // `FeaturesBlock` (BasicPDPModuleAssembly), and the TP toolbox map has NO
        // `features` key, so this is a clean add (no FeaturesModule trap).
        features: FeaturesBlock,

        // `HTML` (uppercase key) -> shared RawHTMLBlock. The serialized value is
        // pre-wrapped in `<div class="raw-html-block">` (RawHTML.__new__), and
        // the component strips + re-applies that single wrapper, reproducing
        // `{% include_block %}` of a RawHTMLBlock.
        HTML: (block: { value: Immutable<RawHTMLBlockValue> }) => (
            <RawHTMLBlock value={block.value} />
        ),

        // `spotlight` -> TP SpotlightModule (the OwnerSpotlight snippet
        // render with an empty `--` brand-color suffix).
        spotlight: SpotlightModule,

        // `value_prop_bar` -> ValuePropsBarBlock (prop is `valuePropsData`).
        // Same prop-rename wrapper as the toolbox `value_prop_bar_block` entry;
        // both keys exist in `PDPModuleAssembly` and both render ValuePropsBar.
        value_prop_bar: (block: {
            value: Immutable<ValuePropBarBlockValue>;
        }) => <ValuePropsBarBlock valuePropsData={block.value} />,

        // `reviews_full` -> TP FullReviewsModule (`.pdp-section` wrapper,
        // `#reviews` SSR island gated on `reviews_brand_id`,
        // `starHasStroke={true}`, CTA gated on `show_cta && page_num_reviews > 0`).
        // The SSR props are looked up per block by `block.id` from the id-keyed
        // `props.reviews` side-channel (the StreamBlock has no `max_num` cap, so
        // each block carries its own props); a missing entry means the component
        // renders only the empty `.pdp-section` wrapper when `reviews` is null.
        reviews_full: (block: {
            id?: string;
            value: Immutable<FullReviewsModuleValue>;
        }) => (
            <FullReviewsModule
                value={block.value}
                reviews={(block.id ? props.reviews[block.id] : null) ?? null}
                page_num_reviews={props.pageNumReviews}
            />
        ),

        // `tab_cards_block` -> shared TabCardsBlock container. Same wrapper /
        // decode as the toolbox map entry, but with the REAL page-derived
        // `initialRootProductID` (threaded from `get_pdp_block_ssr`, mirroring
        // the shared `TabCardsBlock.get_context` computation) instead of
        // the toolbox entry's hardcoded 0 -- the PDP override the toolbox
        // docstring calls for.
        tab_cards_block: (block: { value: Immutable<TabCardsBlockValue> }) => {
            const tabCards = check(
                streamfieldModels.TabCardsBlock.decode(block.value),
            );
            return (
                <section
                    className={getGlobalSpacingClasses(block.value.spacing)}
                >
                    <TabCardsBlock
                        tabCards={tabCards}
                        theme={tabCards.theme.theme || ""}
                        initialRootProductID={isoProductID.wrap(
                            props.tabCardsInitialRootProductID,
                        )}
                    />
                </section>
            );
        },

        // `change_variant_option_block` -> reproduces the shared
        // `pdp-change-variant-option-block.html` wrapper
        // (`#pdp-change-variant-block` + optional `|safe` title) around BOTH the
        // change-ROOT and the change-VARIANT selectors, in that order
        // (title -> root -> variant). Both selectors' SSR props are keyed by the
        // SAME `block.id`: the root from `changeRootSSR`, the variant from
        // `changeVariantSSR`. PDPChangeRootProductBlock self-guards (renders null
        // when < 2 root products), so it is a no-op on single-root pages.
        change_variant_option_block: (block: {
            id?: string;
            value: Immutable<ChangeVariantOptionBlockValue>;
        }) => {
            const ssr = block.id ? props.changeVariantSSR[block.id] : undefined;
            const rootSSR = block.id
                ? props.changeRootSSR[block.id]
                : undefined;
            const optionInputType = block.value.optionInputType ?? "";
            return (
                <div
                    tabIndex={0}
                    id="pdp-change-variant-block"
                    className={`pdp-change-variant-option-block l-capped-width ${optionInputType}`}
                >
                    {block.value.title && (
                        <h2
                            className="pdp-change-variant-option-block__title"
                            dangerouslySetInnerHTML={{
                                __html: block.value.title,
                            }}
                        />
                    )}
                    {rootSSR && (
                        <PDPChangeRootProductBlock
                            inputType={rootSSR.input_type}
                            initialRootProductsData={
                                rootSSR.initial_root_products_data
                            }
                            initialSelectedRootProductId={
                                rootSSR.initial_selected_root_product_id
                            }
                        />
                    )}
                    {ssr && (
                        <PDPChangeVariantOptionBlock
                            attributeCode={ssr.attribute_code as IOptionCode}
                            inputType={ssr.input_type}
                            initialOptionValuesData={
                                ssr.initial_option_values_data
                            }
                            initialSelectedValue={ssr.initial_selected_value}
                        />
                    )}
                </div>
            );
        },

        // `root_product_conditional_block` -> PDPSelectedRootProductConditional
        // Block. No wrapper is added here (the container emits its own gated
        // tabpanel div), so dispatch directly. The gating props come from the
        // id-keyed `rootConditionalSSR` side-channel; the block's `content` (a
        // `RootProductConditionalBlockContent` StreamBlock =
        // BasicPDPModuleAssembly + a nested `variant_conditional_block`) is
        // rendered as real React children through this same `components` map --
        // replacing the JSON `content_segments` path so every
        // nested block hydrates. `components` is captured by reference
        // (define-then-close-over), exactly like the recursive
        // `promo_conditional_block`.
        root_product_conditional_block: (block: {
            id?: string;
            value: Immutable<RootConditionalBlockValue>;
        }) => {
            const ssr = block.id
                ? props.rootConditionalSSR[block.id]
                : undefined;
            if (!ssr) {
                return null;
            }
            return (
                <PDPSelectedRootProductConditionalBlock
                    blockProductID={isoProductID.wrap(ssr.block_product_id)}
                    isInitiallyVisible={ssr.is_initially_visible}
                >
                    <StreamField
                        tagName={React.Fragment}
                        value={block.value.content ?? []}
                        components={components}
                    />
                </PDPSelectedRootProductConditionalBlock>
            );
        },

        // `variant_conditional_block` -> PDPSelectedVariantConditionalBlock.
        // Same children-recursion mechanism as the root conditional (the shared
        // container accepts `children`); its `content` is a
        // `BasicPDPModuleAssembly` (no further conditionals, but it CAN hold
        // `reviews_full`, which resolves in this map via its id-keyed
        // side-channel).
        variant_conditional_block: (block: {
            id?: string;
            value: Immutable<VariantConditionalBlockValue>;
        }) => {
            const ssr = block.id
                ? props.variantConditionalSSR[block.id]
                : undefined;
            if (!ssr) {
                return null;
            }
            return (
                <PDPSelectedVariantConditionalBlock
                    attributeCode={ssr.attribute_code as IOptionCode}
                    attributeValue={ssr.attribute_value}
                    isInitiallyVisible={ssr.is_initially_visible}
                >
                    <StreamField
                        tagName={React.Fragment}
                        value={block.value.content ?? []}
                        components={components}
                    />
                </PDPSelectedVariantConditionalBlock>
            );
        },
    };
    return components;
};

export const ProductPageBody = (props: IProps): React.JSX.Element => {
    const components = buildProductPageBodyComponentsMap(props);

    // Alt-design PDP: drop the top-level blocks the alt-design hero renders
    // separately (gallery_block + value_prop_bar_block), so they are not
    // double-rendered. Default-design + PLP render every top-level block.
    const value =
        props.useDefaultDesign === false
            ? props.value.filter(
                  (block) => !ALT_DESIGN_HERO_BLOCK_TYPES.has(block.type),
              )
            : props.value;

    if (props.wrapBlocks) {
        // PLP whole-field `{% include_block self.modules %}` parity: per
        // top-level block `<div class="w-block-{type} block-{type}">` (Wagtail
        // render_basic; the validated Phase-2 whole-field convention).
        return (
            <>
                {value.map((block) => (
                    <StreamField
                        key={block.id}
                        tagName="div"
                        attrs={{
                            className: `w-block-${block.type} block-${block.type}`,
                        }}
                        value={[block]}
                        components={components}
                    />
                ))}
            </>
        );
    }
    return (
        <StreamField
            tagName={React.Fragment}
            value={value}
            components={components}
        />
    );
};
